import React, { useEffect } from 'react';
import { useKey } from 'react-use';
import useModal from 'hooks/useModal.js';
import useProductUpdates from 'hooks/useProductUpdates.jsx';
import { COLORS, Icon, Tooltip } from '@livecommerce/design-system';
import ProductUpdatesModal from 'components-v3/ProductUpdates/ProductUpdatesModal.jsx';
import { styled } from '@linaria/react';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import HeaderItem from './HeaderItem.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';

const StyledHeaderItem = styled(HeaderItem)`
  border-radius: 4px;
  cursor: pointer;
  display: flow-root;
  &:hover {
    background-color: ${COLORS.dark_600};
  }
  &:hover svg {
    transition: color 0.2s;
    color: ${COLORS.white};
  }
`;

const HeaderItemContents = styled.div`
  display: flex;
  justify-content: center;
  position: relative;

  &:after {
    content: ${(props) => (props.hasNewItems ? '""' : 'none')};
    position: absolute;
    top: 0;
    right: 10px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${COLORS.orange_500};
  }
`;

const ProductUpdatesItem = () => {
  const { isOpen, open, close } = useModal();
  const { t } = useLocalization();
  const { items, isLoading, isError, setSeenItems } = useProductUpdates();
  const hasNewItems = items.some((item) => item.isNew);

  // Close modal on escape key press
  useKey('Escape', () => {
    if (isOpen) {
      close();
      setSeenItems();
      trackingService.track({ eventType: 'product-updates-modal:escape' });
    }
  }, {}, [isOpen, close, setSeenItems]);

  // Auto open the modal if there are new items
  // Wait a little bit to make it slightly less dramatic. This pause also
  // helps to cover over some early flashes that happens when dashboard is
  // initially loading.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (hasNewItems) {
        open();
      }
    }, 2000);
    return () => clearTimeout(handle);
  }, [hasNewItems, open]);

  if (isLoading || isError || !items.length) return null;

  return (
    <>
      <StyledHeaderItem label={t('productUpdates.title')} onClick={open}>
        <HeaderItemContents hasNewItems={hasNewItems}>
          <Tooltip
            tooltipText={t(hasNewItems ? 'productUpdates.newProductUpdatesTooltip' : 'productUpdates.productUpdatesTooltip')}
            id="productUpdatesButtonTooltip"
            isShown={true}
            variant="grey"
            tooltipPosition="left"
          >
            <Icon name="announcement" color={COLORS.greyMediumDark} />
          </Tooltip>
        </HeaderItemContents>
      </StyledHeaderItem>
      <ProductUpdatesModal isOpen={isOpen} onClose={close} />
    </>
  );
};

export default ProductUpdatesItem;
